import { Component } from '@theme/component';

/**
 * Countdown timer component that counts down to a target date/time.
 *
 * @typedef {object} TimerRefs
 * @property {HTMLElement} days - Days display element
 * @property {HTMLElement} hours - Hours display element
 * @property {HTMLElement} minutes - Minutes display element
 * @property {HTMLElement} seconds - Seconds display element
 * @property {HTMLElement} countdown - The countdown container
 * @property {HTMLElement} [expired] - The expired message container
 *
 * @extends {Component<TimerRefs>}
 */
export class TimerComponent extends Component {
  requiredRefs = ['countdown'];

  /** @type {number | undefined} */
  #intervalId;

  connectedCallback() {
    super.connectedCallback();
    this.#tick();
    this.#intervalId = window.setInterval(() => this.#tick(), 1000);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#intervalId) {
      clearInterval(this.#intervalId);
    }
  }

  #tick() {
    const target = this.dataset.targetDate;
    if (!target) return;

    const now = Date.now();
    const end = new Date(target).getTime();
    const diff = end - now;

    if (diff <= 0) {
      this.#handleExpired();
      return;
    }

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
    const minutes = Math.floor((diff / (1000 * 60)) % 60);
    const seconds = Math.floor((diff / 1000) % 60);

    const { days: daysEl, hours: hoursEl, minutes: minutesEl, seconds: secondsEl } = this.refs;

    if (daysEl) daysEl.textContent = String(days).padStart(2, '0');
    if (hoursEl) hoursEl.textContent = String(hours).padStart(2, '0');
    if (minutesEl) minutesEl.textContent = String(minutes).padStart(2, '0');
    if (secondsEl) secondsEl.textContent = String(seconds).padStart(2, '0');
  }

  #handleExpired() {
    if (this.#intervalId) {
      clearInterval(this.#intervalId);
      this.#intervalId = undefined;
    }

    const { countdown, expired } = this.refs;
    const hideWhenExpired = this.dataset.hideWhenExpired === 'true';

    if (hideWhenExpired) {
      this.style.display = 'none';
      return;
    }

    countdown.hidden = true;

    if (expired) {
      expired.hidden = false;
    }
  }
}

if (!customElements.get('timer-component')) {
  customElements.define('timer-component', TimerComponent);
}
